/* LesTresorsDeKanto — global.js
   Header (menu mobile, dropdowns, recherche prédictive), steppers de quantité.
   Vanilla JS, custom elements, compatible Shopify Theme Editor. */

(() => {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Quantity stepper                                                    */
  /* ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const previousValue = this.input.value;
          if (button.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (previousValue !== this.input.value) {
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }
  }
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }

  /* ------------------------------------------------------------------ */
  /* Header: mobile nav, dropdowns, search                               */
  /* ------------------------------------------------------------------ */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.mobileNav = this.querySelector('#MobileNav');
      this.toggleButton = this.querySelector('[data-menu-toggle]');

      // Ombre portée uniquement une fois la page défilée.
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 4);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      if (this.toggleButton && this.mobileNav) {
        this.toggleButton.addEventListener('click', () => this.openMobileNav());
        this.mobileNav.querySelectorAll('[data-menu-close]').forEach((el) => {
          el.addEventListener('click', () => this.closeMobileNav());
        });
        this.mobileNav.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', () => this.closeMobileNav());
        });
      }

      // Desktop dropdowns: open on hover, close on outside click / Escape.
      this.querySelectorAll('[data-nav-details]').forEach((details) => {
        const item = details.closest('.site-nav__item');
        let closeTimer;
        item.addEventListener('mouseenter', () => {
          clearTimeout(closeTimer);
          details.setAttribute('open', '');
        });
        item.addEventListener('mouseleave', () => {
          closeTimer = setTimeout(() => details.removeAttribute('open'), 150);
        });
      });

      document.addEventListener('click', (event) => {
        this.querySelectorAll('details[open]').forEach((details) => {
          if (!details.contains(event.target)) details.removeAttribute('open');
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
        this.closeMobileNav();
      });

      // Indicateur glissant sous les catégories (desktop)
      this.initNavIndicator();

      // Search modal focus
      const searchDetails = this.querySelector('[data-search-details]');
      if (searchDetails) {
        searchDetails.addEventListener('toggle', () => {
          if (searchDetails.open) {
            const input = searchDetails.querySelector('input[type="search"]');
            if (input) setTimeout(() => input.focus(), 30);
          }
        });
        this.querySelectorAll('[data-search-open]').forEach((el) => {
          el.addEventListener('click', () => searchDetails.setAttribute('open', ''));
        });
        this.querySelectorAll('[data-search-close]').forEach((el) => {
          el.addEventListener('click', () => searchDetails.removeAttribute('open'));
        });
        searchDetails.addEventListener('toggle', () => {
          document.body.classList.toggle('search-open', searchDetails.open);
        });
      }
    }

    initNavIndicator() {
      const nav = this.querySelector('.site-nav');
      const indicator = nav && nav.querySelector('.site-nav__indicator');
      if (!nav || !indicator) return;
      const links = [...nav.querySelectorAll(':scope > .site-nav__item > .site-nav__link, :scope > .site-nav__item > details > summary.site-nav__link')];
      if (!links.length) return;
      const active = links.find((l) => l.classList.contains('site-nav__link--active'));

      const moveTo = (link) => {
        if (!link) { indicator.style.opacity = '0'; return; }
        const navRect = nav.getBoundingClientRect();
        const rect = link.getBoundingClientRect();
        const pad = parseFloat(getComputedStyle(link).paddingLeft) || 0;
        indicator.style.opacity = '1';
        indicator.style.width = Math.max(0, rect.width - pad * 2) + 'px';
        indicator.style.transform = 'translateX(' + (rect.left - navRect.left + pad) + 'px)';
      };

      links.forEach((link) => {
        const item = link.closest('.site-nav__item');
        item.addEventListener('mouseenter', () => moveTo(link));
        link.addEventListener('focus', () => moveTo(link));
      });
      nav.addEventListener('mouseleave', () => moveTo(active));
      window.addEventListener('resize', () => moveTo(active), { passive: true });
      // Position initiale sans animation
      indicator.style.transition = 'none';
      moveTo(active);
      requestAnimationFrame(() => requestAnimationFrame(() => (indicator.style.transition = '')));
    }

    openMobileNav() {
      this.mobileNav.hidden = false;
      this.toggleButton.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      const firstLink = this.mobileNav.querySelector('a, button');
      if (firstLink) firstLink.focus();
    }

    closeMobileNav() {
      if (!this.mobileNav || this.mobileNav.hidden) return;
      this.mobileNav.hidden = true;
      this.toggleButton.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  }
  if (!customElements.get('header-menu')) {
    customElements.define('header-menu', HeaderMenu);
  }

  /* ------------------------------------------------------------------ */
  /* Predictive search                                                   */
  /* ------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.predictive-search');
      if (!this.input || !this.results) return;

      this.input.addEventListener(
        'input',
        this.debounce(() => this.onChange(), 250)
      );
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    async onChange() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.results.hidden = true;
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      try {
        const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[limit]=6&section_id=predictive-search`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.status);
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const content = parsed.querySelector('#predictive-search-results-content');
        this.results.innerHTML = content ? content.innerHTML : '';
        this.results.hidden = !content;
        this.input.setAttribute('aria-expanded', content ? 'true' : 'false');
      } catch (error) {
        this.results.hidden = true;
      }
    }
  }
  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }

  /* ------------------------------------------------------------------ */
  /* Facets: auto-submit sort select                                     */
  /* ------------------------------------------------------------------ */
  class FacetFiltersForm extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      const sortSelect = this.querySelector('[data-sort-select]');
      if (form && sortSelect) {
        sortSelect.addEventListener('change', () => form.submit());
      }

      // Ferme le panneau de filtres au clic extérieur ou sur Échap.
      const disclosure = this.querySelector('.facets__disclosure');
      if (disclosure) {
        document.addEventListener('click', (event) => {
          if (disclosure.open && !disclosure.contains(event.target)) {
            disclosure.removeAttribute('open');
          }
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') disclosure.removeAttribute('open');
        });
      }
    }
  }
  if (!customElements.get('facet-filters-form')) {
    customElements.define('facet-filters-form', FacetFiltersForm);
  }

  /* ------------------------------------------------------------------ */
  /* Theme Editor: keep drawers coherent when sections reload            */
  /* ------------------------------------------------------------------ */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      document.body.style.overflow = '';
      document.body.classList.remove('cart-drawer-open');
    });
  }
})();
